import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingBag, Package, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const OTC_IMG = "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxtZWRpY2FsJTIwcGlsbHMlMjBkZWxpdmVyeSUyMHBhY2thZ2V8ZW58MHx8fHwxNzg3MTIyMjc0fDA&ixlib=rb-4.1.0&q=85";

export default function OTCStore() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const [method, setMethod] = useState("pickup");
  const [qty, setQty] = useState(1);
  const [info, setInfo] = useState({ customer_name: "", phone: "", email: "", address: "" });
  const [loyalty, setLoyalty] = useState(null);
  const [redeem, setRedeem] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (user && user.role === "patient") {
      setInfo((i) => ({ ...i, customer_name: user.name, phone: user.phone || "", email: user.email, address: user.address || "" }));
      api.get("/loyalty/me").then(({ data }) => setLoyalty(data)).catch(() => {});
    }
  }, [user]);

  async function load() {
    try { const { data } = await api.get("/otc/products"); setProducts(data); } catch { /* ignore */ }
  }

  function openReserve(p) { setActive(p); setQty(1); setMethod("pickup"); setRedeem(false); }

  async function reserve(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/otc/reservations", {
        customer_name: info.customer_name, phone: info.phone, email: info.email,
        product_id: active.id, product_name: active.name, quantity: Number(qty),
        method, address: method === "delivery" ? info.address : null, redeem_points: redeem,
      });
      if (data.discount_applied > 0) toast.success(`Reservation submitted with $${data.discount_applied.toFixed(2)} loyalty discount applied!`);
      else toast.success("Reservation submitted! We'll have it ready for you.");
      if (user && user.role === "patient") api.get("/loyalty/me").then(({ data: l }) => setLoyalty(l)).catch(() => {});
      setActive(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reserve.");
    } finally { setLoading(false); }
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-10 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><ShoppingBag className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">OTC Store</h1>
          <p className="text-sm text-muted-foreground">Reserve over-the-counter products for pickup or delivery.</p>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="otc-empty">
          <Package className="mx-auto mb-3 h-10 w-10 opacity-40" />
          No products available yet. Please check back soon.
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <div key={p.id} data-testid={`otc-product-${p.id}`} className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-shadow hover:shadow-lg">
              <img src={p.image || OTC_IMG} alt={p.name} className="h-44 w-full object-cover" />
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading text-lg font-bold">{p.name}</h3>
                  <span className="whitespace-nowrap font-heading text-lg font-bold text-primary">${Number(p.price).toFixed(2)}</span>
                </div>
                <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">{p.units}</p>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{p.uses}</p>
                <Button onClick={() => openReserve(p)} data-testid={`otc-reserve-${p.id}`} className="mt-4 rounded-full">Reserve</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reserve {active?.name}</DialogTitle></DialogHeader>
          <form onSubmit={reserve} className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setMethod("pickup")} data-testid="otc-method-pickup"
                className={`rounded-lg border py-2 text-sm font-medium ${method === "pickup" ? "border-primary bg-primary text-white" : "border-border"}`}>Pickup</button>
              <button type="button" onClick={() => setMethod("delivery")} data-testid="otc-method-delivery"
                className={`rounded-lg border py-2 text-sm font-medium ${method === "delivery" ? "border-primary bg-primary text-white" : "border-border"}`}>Delivery</button>
            </div>
            <div className="space-y-2"><Label>Name</Label><Input data-testid="otc-name" value={info.customer_name} onChange={(e) => setInfo({ ...info, customer_name: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Phone</Label><Input data-testid="otc-phone" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} required /></div>
              <div className="space-y-2"><Label>Quantity</Label><Input type="number" min="1" data-testid="otc-qty" value={qty} onChange={(e) => setQty(e.target.value)} required /></div>
            </div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="otc-email" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} required /></div>
            {method === "delivery" && (
              <div className="space-y-2"><Label>Delivery address</Label><Input data-testid="otc-address" value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} required /></div>
            )}
            {loyalty?.reward_available && (
              <label className="flex items-center gap-3 rounded-lg border border-primary/30 bg-secondary/50 p-3 text-sm" data-testid="otc-loyalty-redeem-wrap">
                <Checkbox checked={redeem} onCheckedChange={(v) => setRedeem(!!v)} data-testid="otc-loyalty-redeem" />
                <span>Apply my <strong>$5 loyalty reward</strong> ({loyalty.points} points available)</span>
              </label>
            )}
            <Button type="submit" disabled={loading} data-testid="otc-submit" className="w-full rounded-full">
              {loading ? "Submitting..." : `Reserve for ${method === "pickup" ? "Pickup" : "Delivery"}`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
